import { type UseQueryOptions, useQuery } from '@tanstack/react-query';

import { get } from '@wecasa/common/src/api/requester';
import { UNIVERSES } from '@wecasa/common/src/constants/universes';
import type { UniverseReference } from '@wecasa/common/src/types/commons';

const CESU_WALLET_QUERY_KEY = 'cesu_wallet';

type CesuWalletResponse = {
  // Amount in the user's e-CESU wallet, in cents.
  // null when the universe is not eligible, 0 when the wallet is empty or absent.
  cesu_amount: number | null;
};

type GetCesuWalletParams = Pick<UseQueryOptions<CesuWalletResponse>, 'enabled'> & {
  universe?: UniverseReference;
};

export const useGetCesuWalletQuery = ({ enabled = true, universe = UNIVERSES.CLEANING }: GetCesuWalletParams = {}) => {
  const {
    data,
    isLoading: isLoadingCesuAmount,
    error: cesuAmountError,
  } = useQuery<CesuWalletResponse, Error>({
    queryKey: [CESU_WALLET_QUERY_KEY, universe],
    queryFn: async () => {
      const response = await get('/customer/cesu_wallet', { query: { universe } });
      return response.data;
    },
    enabled,
    // The funnel waits for this answer on the address step, so a failing wallet must settle at once.
    // CESU is a side feature: retrying it would hold the customer on a spinner for the whole backoff.
    retry: false,
    // Screens hide themselves while the wallet loads, so the basket preview that also reads the wallet
    // mounts only once it has settled. Refetching on that mount puts the query back to loading, the
    // preview unmounts, and the failure loops forever. Keep the failed answer across those remounts.
    retryOnMount: false,
  });

  return { cesuAmount: data?.cesu_amount, isLoadingCesuAmount, cesuAmountError };
};
